import { reportError } from "@/utils/reportError";
import { trackContestEntered } from '@/utils/analytics';
import { getTodayCH } from '@/utils/date';
import { supabase } from '@/utils/supabase';
import { useCallback, useEffect, useState } from 'react';

interface ContestEntry {
  id: string;
  email: string;
  createdAt: string;
}

interface SubmitResult {
  success: boolean;
  error?: 'already_entered' | 'invalid_email' | 'consent_required' | 'not_authenticated' | 'unknown';
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function useContestEntry() {
  const [hasEntered, setHasEntered] = useState<boolean>(true); // default true to hide CTA until checked
  // Vrai si l'utilisateur a AU MOINS une participation (toutes dates).
  // Sert au « 1ᵉʳ tour de roue = cadeau garanti » (utils/wheel.ts).
  const [hasEverEntered, setHasEverEntered] = useState<boolean>(true); // défaut prudent, comme hasEntered
  const [entry, setEntry] = useState<ContestEntry | null>(null);
  const [loading, setLoading] = useState(true);

  const checkEntry = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        setHasEntered(false);
        setEntry(null);
        return;
      }

      // Un seul appel : dernière participation, toutes dates confondues.
      // → hasEntered = participation du jour ; hasEverEntered = historique.
      const { data, error } = await supabase
        .from('contest_entries')
        .select('id, email, created_at, entry_date')
        .eq('user_id', user.id)
        .order('entry_date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        reportError(error);
        setHasEntered(false);
        return;
      }

      setHasEverEntered(!!data);
      if (data && data.entry_date === getTodayCH()) {
        setHasEntered(true);
        setEntry({ id: data.id, email: data.email, createdAt: data.created_at });
      } else {
        setHasEntered(false);
        setEntry(null);
      }
    } catch (error) {
      reportError(error);
      setHasEntered(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkEntry();
  }, [checkEntry]);

  const submitEntry = useCallback(
    async (email: string, consent: boolean): Promise<SubmitResult> => {
      const normalizedEmail = email.trim().toLowerCase();
      if (!EMAIL_RE.test(normalizedEmail)) return { success: false, error: 'invalid_email' };
      if (!consent) return { success: false, error: 'consent_required' };

      try {
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user;
        if (!user) return { success: false, error: 'not_authenticated' };

        const { data, error } = await supabase
          .from('contest_entries')
          .insert({
            user_id: user.id,
            email: normalizedEmail,
            consent_rgpd: true,
          })
          .select('id, email, created_at')
          .single();

        if (error) {
          // 23505 = unique_violation → déjà participé
          if (error.code === '23505') {
            setHasEntered(true);
            setHasEverEntered(true);
            return { success: false, error: 'already_entered' };
          }
          reportError(error);
          return { success: false, error: 'unknown' };
        }

        setHasEntered(true);
        setHasEverEntered(true);
        setEntry({ id: data.id, email: data.email, createdAt: data.created_at });
        trackContestEntered();
        return { success: true };
      } catch (error) {
        reportError(error);
        return { success: false, error: 'unknown' };
      }
    },
    []
  );

  return {
    hasEntered,
    hasEverEntered,
    entry,
    loading,
    submitEntry,
    refetch: checkEntry,
  };
}
